import { useState, useEffect } from 'react';
import { Download, X, GitCompare } from 'lucide-react';
import { ProviderInfo } from '../types/provider';
import { ModelInfo, AgentType, AgentModelOverride } from '../types/models';
import { EmbeddingIndexInfo } from '../types/embedding';
import { SystemPromptSummary } from '../types/systemPrompt';
import { EvaluationProviderConfig } from '../types/evaluation';
import { ApiService } from '../services/apiService';
import { TemplatePicker, TemplateData } from './TemplatePicker';
import { EmbeddingIndexSelector } from './EmbeddingIndexSelector';
import { TextComparisonDialog, ComparisonItem } from './TextComparisonDialog';
import { SystemPromptTableDropdown } from './SystemPromptTableDropdown';

interface EvaluationProviderEditorProps {
  value: EvaluationProviderConfig;
  onChange: (value: EvaluationProviderConfig) => void;
  disabled?: boolean;
  validationErrors?: Record<string, string>;
  previousConfig?: EvaluationProviderConfig;
  previousProviderIndex?: number;
  onCopyFromPrevious?: () => void;
}

interface CustomConfig {
  id: string;
  name: string;
  config: Record<AgentType, AgentModelOverride | undefined>;
  maxOpinions?: number;
  maxPassagesPerDocument?: number;
  embeddingIndex?: string;
}

/** Agent types supported by evaluation provider configs (excludes deprecated summarizer) */
type EvalAgentType = 'orchestrator' | 'researcher' | 'reducer' | 'writer' | 'relevancechecker';

const AGENT_LABELS: Record<AgentType, string> = {
  orchestrator: 'Orchestrator',
  researcher: 'Researcher',
  reducer: 'Reducer',
  writer: 'Writer',
  summarizer: 'Summarizer',
  relevancechecker: 'Relevance Checker'
};

const CUSTOM_CONFIGS_KEY = 'custom-model-configs';

export const EvaluationProviderEditor = ({
  value,
  onChange,
  disabled = false,
  validationErrors = {},
  previousConfig,
  previousProviderIndex,
  onCopyFromPrevious
}: EvaluationProviderEditorProps) => {
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [customConfigs, setCustomConfigs] = useState<CustomConfig[]>([]);
  const [availableModels, setAvailableModels] = useState<Record<string, ModelInfo[]>>({});
  const [embeddingIndexes, setEmbeddingIndexes] = useState<EmbeddingIndexInfo[]>([]);
  const [systemPrompts, setSystemPrompts] = useState<SystemPromptSummary[]>([]);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showEmbeddingIndexDialog, setShowEmbeddingIndexDialog] = useState(false);
  const [showPromptComparison, setShowPromptComparison] = useState(false);
  const [promptComparisonItems, setPromptComparisonItems] = useState<ComparisonItem[]>([]);
  const [editingAgent, setEditingAgent] = useState<EvalAgentType | null>(null);
  const [tempConfig, setTempConfig] = useState<AgentModelOverride>({
    providerType: '',
    modelId: '',
    temperature: undefined,
    systemPromptHash: undefined
  });

  const apiService = ApiService.getInstance();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [providersData, modelsData, embeddingIndexesData, systemPromptsData] = await Promise.all([
        apiService.getProviders(),
        apiService.getAvailableModels(),
        apiService.getEmbeddingIndexes(),
        apiService.getSystemPrompts(false)
      ]);
      setProviders(providersData);
      setAvailableModels(modelsData.modelsByProvider || {});
      setEmbeddingIndexes(embeddingIndexesData);
      setSystemPrompts(systemPromptsData);

      // Load custom configs from localStorage
      const stored = localStorage.getItem(CUSTOM_CONFIGS_KEY);
      if (stored) {
        setCustomConfigs(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const handleTemplateSelected = (template: TemplateData) => {
    // Copy all data from template to current value
    onChange({
      ...value,
      name: value.name || template.name, // Keep existing name if set
      embeddingIndex: template.embeddingIndex,
      maxOpinions: template.maxOpinions,
      maxPassagesPerDocument: template.maxPassagesPerDocument,
      useReducer: template.useReducer,
      orchestrator: template.agents.orchestrator,
      researcher: template.agents.researcher,
      reducer: template.agents.reducer,
      writer: template.agents.writer,
      providerId: undefined // Clear providerId since we're using custom config format
    });
    setShowTemplatePicker(false);
  };

  const handleCellClick = (agentType: EvalAgentType) => {
    if (disabled) return;

    const existing = value[agentType];

    // Filter system prompts by agent type and get the newest one as default
    const agentSystemPrompts = systemPrompts.filter(p => p.agentType === agentType);
    const newestPromptHash = agentSystemPrompts.length > 0 ? agentSystemPrompts[0].hash : undefined;

    setTempConfig(existing || {
      providerType: '',
      modelId: '',
      temperature: undefined,
      topP: undefined,
      seed: undefined,
      frequencyPenalty: undefined,
      presencePenalty: undefined,
      systemPromptHash: newestPromptHash
    });
    setEditingAgent(agentType);
  };

  const handleProviderChange = (providerType: string) => {
    setTempConfig({
      providerType,
      modelId: '',
      temperature: undefined,
      topP: undefined,
      seed: undefined,
      frequencyPenalty: undefined,
      presencePenalty: undefined,
      systemPromptHash: tempConfig.systemPromptHash
    });
  };

  const handleModelChange = (modelId: string) => {
    const models = availableModels[tempConfig.providerType] || [];
    const selectedModel = models.find(m => m.modelId === modelId);
    setTempConfig({
      ...tempConfig,
      modelId,
      temperature: selectedModel?.isTemperatureAllowed ? selectedModel.defaultTemperature : undefined,
      topP: selectedModel?.defaultTopP,
      seed: selectedModel?.defaultSeed,
      frequencyPenalty: selectedModel?.defaultFrequencyPenalty,
      presencePenalty: selectedModel?.defaultPresencePenalty
    });
  };

  const handleSaveAgentConfig = () => {
    if (!editingAgent) return;

    if (!tempConfig.providerType || !tempConfig.modelId) {
      alert('Please select both provider and model');
      return;
    }

    onChange({
      ...value,
      [editingAgent]: tempConfig
    });

    setEditingAgent(null);
  };

  const handleCancelEdit = () => {
    setEditingAgent(null);
    setTempConfig({
      providerType: '',
      modelId: '',
      temperature: undefined,
      topP: undefined,
      seed: undefined,
      frequencyPenalty: undefined,
      presencePenalty: undefined,
      systemPromptHash: undefined
    });
  };

  const handleOpenPromptComparison = () => {
    const agentPrompts = systemPrompts.filter(p => editingAgent && p.agentType === editingAgent);
    const items: ComparisonItem[] = agentPrompts.map(p => ({
      id: p.hash,
      label: p.description,
      description: p.purpose,
      content: '',
      createdAt: p.createdAt
    }));
    setPromptComparisonItems(items);
    setShowPromptComparison(true);
  };

  const fetchPromptContent = async (hash: string): Promise<string> => {
    const prompt = await apiService.getSystemPromptByHash(hash);
    return prompt?.content || '[Content not found]';
  };

  const handlePromptComparisonApprove = (hash: string) => {
    setTempConfig({ ...tempConfig, systemPromptHash: hash || undefined });
    setShowPromptComparison(false);
  };

  const handleSelectEmbeddingIndex = (indexName: string) => {
    onChange({
      ...value,
      embeddingIndex: indexName || undefined
    });
    setShowEmbeddingIndexDialog(false);
  };

  const providerTypes = Object.keys(availableModels);
  const models = tempConfig.providerType ? (availableModels[tempConfig.providerType] || []) : [];
  const modelInfo = models.find(m => m.modelId === tempConfig.modelId);

  return (
    <div data-component="EvaluationProviderEditor" className="space-y-4">
      {/* Copy from Previous and Load from Template Buttons */}
      <div className="flex justify-end gap-2">
        {previousConfig && onCopyFromPrevious && previousProviderIndex !== undefined && (
          <button
            type="button"
            onClick={onCopyFromPrevious}
            disabled={disabled}
            className="flex items-center gap-1 px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
            title={`Copy all settings from Provider ${previousProviderIndex + 1}${previousConfig.name ? `: ${previousConfig.name}` : ''}`}
          >
            <Download className="w-4 h-4" />
            Copy from Provider {previousProviderIndex + 1}
          </button>
        )}
        <button
          type="button"
          onClick={() => setShowTemplatePicker(true)}
          disabled={disabled}
          className="flex items-center gap-1 px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          Load from Template
        </button>
      </div>

      {/* Custom Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Custom Name *
        </label>
        <input
          type="text"
          value={value.name}
          onChange={(e) => onChange({ ...value, name: e.target.value })}
          disabled={disabled}
          placeholder="e.g., Fast Config"
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 ${
            validationErrors.name ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {validationErrors.name && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.name}</p>
        )}
      </div>

      {/* Configuration Table */}
      <div className="border border-gray-200 rounded-lg overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-2 text-left font-semibold text-gray-700">Setting</th>
              <th className="px-4 py-2 text-left font-semibold text-gray-700">Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {/* Embedding Index */}
            <tr className="bg-white hover:bg-gray-50">
              <td className="px-4 py-2 font-medium text-gray-900">Embedding Index</td>
              <td className="px-4 py-2">
                <span
                  className={`${disabled ? 'text-gray-600' : 'cursor-pointer text-blue-600 hover:text-blue-800 hover:underline'}`}
                  onClick={() => !disabled && setShowEmbeddingIndexDialog(true)}
                  title={embeddingIndexes.find(idx => idx.indexName === value.embeddingIndex)?.description || 'Click to select embedding index'}
                >
                  {value.embeddingIndex || embeddingIndexes.find(idx => idx.isDefault)?.indexName || 'Default'}
                </span>
              </td>
            </tr>

            {/* Agent Configurations */}
            {(['orchestrator', 'researcher', 'relevancechecker', 'reducer', 'writer'] as EvalAgentType[]).map(agentType => {
              const agentConfig = value[agentType];
              return (
                <tr
                  key={agentType}
                  className={`${disabled ? 'bg-white' : 'bg-white hover:bg-gray-50 cursor-pointer'}`}
                  onClick={() => handleCellClick(agentType)}
                >
                  <td className="px-4 py-2 font-medium text-gray-900">{AGENT_LABELS[agentType]}</td>
                  <td className="px-4 py-2">
                    {agentConfig?.providerType && agentConfig?.modelId ? (
                      <div className="text-xs">
                        <div className="font-medium text-gray-900">{agentConfig.providerType}</div>
                        <div className="text-gray-700">{agentConfig.modelId}</div>
                        <div className="text-gray-600 mt-0.5 space-y-0.5">
                          {agentConfig.temperature !== undefined && agentConfig.temperature !== null && (
                            <div>T: {agentConfig.temperature.toFixed(2)}</div>
                          )}
                          {agentConfig.topP !== undefined && agentConfig.topP !== null && (
                            <div>TopP: {agentConfig.topP.toFixed(2)}</div>
                          )}
                          {agentConfig.seed !== undefined && agentConfig.seed !== null && (
                            <div>Seed: {agentConfig.seed}</div>
                          )}
                          {agentConfig.frequencyPenalty !== undefined && agentConfig.frequencyPenalty !== null && agentConfig.frequencyPenalty !== 0 && (
                            <div>FP: {agentConfig.frequencyPenalty.toFixed(2)}</div>
                          )}
                          {agentConfig.presencePenalty !== undefined && agentConfig.presencePenalty !== null && agentConfig.presencePenalty !== 0 && (
                            <div>PP: {agentConfig.presencePenalty.toFixed(2)}</div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">{disabled ? '-' : 'Click to configure'}</span>
                    )}
                  </td>
                </tr>
              );
            })}

            {/* Use Reducer */}
            <tr className="bg-white hover:bg-gray-50">
              <td className="px-4 py-2 font-medium text-gray-900">Use Reducer</td>
              <td className="px-4 py-2">
                <input
                  type="checkbox"
                  checked={value.useReducer || false}
                  onChange={(e) => onChange({ ...value, useReducer: e.target.checked })}
                  disabled={disabled}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed"
                />
              </td>
            </tr>

            {/* HITL */}
            <tr className="bg-white hover:bg-gray-50">
              <td className="px-4 py-2 font-medium text-gray-900">
                HITL
                <span
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs text-gray-500 bg-gray-200 rounded-full cursor-help"
                  title="Human-in-the-Loop: When enabled, the AI will ask clarifying questions instead of guessing when your query is unclear."
                >
                  i
                </span>
              </td>
              <td className="px-4 py-2">
                <input
                  type="checkbox"
                  checked={value.enableHitl !== false}
                  onChange={(e) => onChange({ ...value, enableHitl: e.target.checked })}
                  disabled={disabled}
                  className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500 cursor-pointer disabled:cursor-not-allowed"
                />
              </td>
            </tr>

            {/* FOLIO */}
            <tr className="bg-white hover:bg-gray-50">
              <td className="px-4 py-2 font-medium text-gray-900">
                FOLIO Threshold
                <span
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs text-gray-500 bg-gray-200 rounded-full cursor-help"
                  title="FOLIO runs when orchestrator complexity score > this value. 0 = always, 10 = disabled."
                >
                  i
                </span>
              </td>
              <td className="px-4 py-2">
                <input
                  type="number"
                  min="0"
                  max="10"
                  value={value.folioThreshold ?? 6}
                  onChange={(e) => onChange({ ...value, folioThreshold: Math.max(0, Math.min(10, parseInt(e.target.value) || 0)) })}
                  disabled={disabled}
                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100"
                  title="FOLIO runs when orchestrator complexity score > this value. 0 = always, 10 = disabled."
                />
              </td>
            </tr>

            {/* Relevance Check */}
            <tr className="bg-white hover:bg-gray-50">
              <td className="px-4 py-2 font-medium text-gray-900">
                Relevance Check
                <span
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs text-gray-500 bg-gray-200 rounded-full cursor-help"
                  title="Relevance Check: When enabled, the RelevanceChecker agent scores and filters search results before passing to the Writer. Disabled by default."
                >
                  i
                </span>
              </td>
              <td className="px-4 py-2">
                <input
                  type="checkbox"
                  checked={value.enableRelevanceCheck || false}
                  onChange={(e) => onChange({ ...value, enableRelevanceCheck: e.target.checked })}
                  disabled={disabled}
                  className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500 cursor-pointer disabled:cursor-not-allowed"
                />
              </td>
            </tr>

            {/* Max Supporting */}
            <tr className="bg-white hover:bg-gray-50">
              <td className="px-4 py-2 font-medium text-gray-900">Max Supporting</td>
              <td className="px-4 py-2">
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={value.maxOpinions || 5}
                  onChange={(e) => {
                    const raw = parseInt(e.target.value) || 1;
                    const clamped = Math.max(1, Math.min(50, raw));
                    onChange({ ...value, maxOpinions: clamped });
                  }}
                  disabled={disabled}
                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  title="Max supporting opinions. Default: 5. Range: 1–50."
                />
              </td>
            </tr>

            {/* Max Contrary */}
            <tr className="bg-white hover:bg-gray-50">
              <td className="px-4 py-2 font-medium text-gray-900">Max Contrary</td>
              <td className="px-4 py-2">
                <input
                  type="number"
                  min="1"
                  max="25"
                  value={value.maxContraryOpinions || Math.ceil((value.maxOpinions || 5) / 2)}
                  onChange={(e) => {
                    const raw = parseInt(e.target.value) || 1;
                    const clamped = Math.max(1, Math.min(25, raw));
                    onChange({ ...value, maxContraryOpinions: clamped });
                  }}
                  disabled={disabled}
                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  title="Max contrary opinions. Default: ceil(supporting/2). Range: 1–25."
                />
              </td>
            </tr>

            {/* Max Passages */}
            <tr className="bg-white hover:bg-gray-50">
              <td className="px-4 py-2 font-medium text-gray-900">Max Passages</td>
              <td className="px-4 py-2">
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={value.maxPassagesPerDocument || 3}
                  onChange={(e) => {
                    const raw = parseInt(e.target.value) || 1;
                    const clamped = Math.max(1, Math.min(20, raw));
                    onChange({ ...value, maxPassagesPerDocument: clamped });
                  }}
                  disabled={disabled}
                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  title="Max passages per document. Default: 3. Range: 1–20."
                />
              </td>
            </tr>

            {/* Min Score */}
            <tr className="bg-white hover:bg-gray-50">
              <td className="px-4 py-2 font-medium text-gray-900">
                Min Score
                <span
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs text-gray-500 bg-gray-200 rounded-full cursor-help"
                  title="Minimum Elasticsearch score threshold. Results below this are discarded. Default: 0.7. Recommended: 0.0–1.0."
                >
                  i
                </span>
              </td>
              <td className="px-4 py-2">
                <input
                  type="number"
                  min="0.01"
                  max="10"
                  step="0.01"
                  value={value.minimumSearchScore ?? 0.7}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0.7;
                    if (val > 0) onChange({ ...value, minimumSearchScore: val });
                  }}
                  disabled={disabled}
                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  title="Minimum ES score threshold. Default: 0.7. Recommended: 0.0–1.0."
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Template Picker Dialog */}
      <TemplatePicker
        isOpen={showTemplatePicker}
        providers={providers}
        customConfigs={customConfigs}
        embeddingIndexes={embeddingIndexes}
        onSelect={handleTemplateSelected}
        onClose={() => setShowTemplatePicker(false)}
      />

      {/* Embedding Index Selector Dialog */}
      <EmbeddingIndexSelector
        isOpen={showEmbeddingIndexDialog}
        embeddingIndexes={embeddingIndexes}
        currentSelection={value.embeddingIndex}
        onSelect={handleSelectEmbeddingIndex}
        onClose={() => setShowEmbeddingIndexDialog(false)}
      />

      {/* Agent Configuration Popup */}
      {editingAgent && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={handleCancelEdit} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-2">
            <div className="bg-white rounded-lg shadow-xl max-h-[90vh] flex flex-col" style={{ width: '80vw' }}>
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0 bg-white">
                <h3 className="text-lg font-semibold text-gray-900">
                  Configure {AGENT_LABELS[editingAgent]}
                </h3>
                <button onClick={handleCancelEdit} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="px-6 py-4 space-y-4 overflow-auto flex-1">
                {/* Provider Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Provider
                  </label>
                  <select
                    value={tempConfig.providerType}
                    onChange={(e) => handleProviderChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Provider</option>
                    {providerTypes.map(provider => (
                      <option key={provider} value={provider}>{provider}</option>
                    ))}
                  </select>
                </div>

                {/* Model Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Model
                  </label>
                  <select
                    value={tempConfig.modelId}
                    onChange={(e) => handleModelChange(e.target.value)}
                    disabled={!tempConfig.providerType}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  >
                    <option value="">Select Model</option>
                    {models.map(model => (
                      <option key={model.modelId} value={model.modelId}>
                        {model.displayName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* System Prompt Selection */}
                <div data-flow="system-prompt-selector">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    System Prompt (Optional)
                  </label>
                  <div className="flex gap-2">
                    <SystemPromptTableDropdown
                      prompts={systemPrompts.filter(p => editingAgent && p.agentType === editingAgent)}
                      selectedHash={tempConfig.systemPromptHash || ''}
                      onSelect={(hash) => setTempConfig({ ...tempConfig, systemPromptHash: hash || undefined })}
                    />
                    {systemPrompts.filter(p => editingAgent && p.agentType === editingAgent).length >= 2 && (
                      <button
                        onClick={handleOpenPromptComparison}
                        className="flex items-center gap-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-200"
                        title="Compare system prompt versions"
                      >
                        <GitCompare className="w-4 h-4" />
                        Compare
                      </button>
                    )}
                  </div>
                  {tempConfig.systemPromptHash && (
                    <p className="text-xs text-gray-600 mt-1">
                      {systemPrompts.find(p => p.hash === tempConfig.systemPromptHash)?.purpose || ''}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Select a custom system prompt to override the default agent instructions
                  </p>
                </div>

                {/* Temperature Slider */}
                {modelInfo?.isTemperatureAllowed && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Temperature: {tempConfig.temperature?.toFixed(2) ?? modelInfo.defaultTemperature.toFixed(2)}
                    </label>
                    <input
                      type="range"
                      min={modelInfo.minTemperature}
                      max={modelInfo.maxTemperature}
                      step={0.01}
                      value={tempConfig.temperature ?? modelInfo.defaultTemperature}
                      onChange={(e) => setTempConfig({ ...tempConfig, temperature: parseFloat(e.target.value) })}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{modelInfo.minTemperature}</span>
                      <span>Default: {modelInfo.defaultTemperature.toFixed(2)}</span>
                      <span>{modelInfo.maxTemperature}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Controls randomness. Lower = more deterministic, Higher = more creative
                    </p>
                  </div>
                )}

                {/* TopP Slider */}
                {modelInfo?.defaultTopP !== undefined && modelInfo?.defaultTopP !== null && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Top P (Nucleus Sampling): {tempConfig.topP?.toFixed(2) ?? modelInfo.defaultTopP.toFixed(2)}
                    </label>
                    <input
                      type="range"
                      min={modelInfo.minTopP ?? 0}
                      max={modelInfo.maxTopP ?? 1}
                      step={0.01}
                      value={tempConfig.topP ?? modelInfo.defaultTopP}
                      onChange={(e) => setTempConfig({ ...tempConfig, topP: parseFloat(e.target.value) })}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{modelInfo.minTopP ?? 0}</span>
                      <span>Default: {modelInfo.defaultTopP.toFixed(2)}</span>
                      <span>{modelInfo.maxTopP ?? 1}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Limits token selection to top probability mass. Lower = more focused
                    </p>
                  </div>
                )}

                {/* Seed Input */}
                {modelInfo?.defaultSeed !== undefined && modelInfo?.defaultSeed !== null && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Seed (for deterministic outputs)
                    </label>
                    <input
                      type="number"
                      value={tempConfig.seed ?? modelInfo.defaultSeed}
                      onChange={(e) => setTempConfig({ ...tempConfig, seed: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={`Default: ${modelInfo.defaultSeed}`}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Fixed seed makes outputs more reproducible (default: {modelInfo.defaultSeed})
                    </p>
                  </div>
                )}

                {/* Frequency Penalty Slider */}
                {modelInfo?.defaultFrequencyPenalty !== undefined && modelInfo?.defaultFrequencyPenalty !== null && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Frequency Penalty: {tempConfig.frequencyPenalty?.toFixed(2) ?? modelInfo.defaultFrequencyPenalty.toFixed(2)}
                    </label>
                    <input
                      type="range"
                      min={modelInfo.minFrequencyPenalty ?? -2}
                      max={modelInfo.maxFrequencyPenalty ?? 2}
                      step={0.01}
                      value={tempConfig.frequencyPenalty ?? modelInfo.defaultFrequencyPenalty}
                      onChange={(e) => setTempConfig({ ...tempConfig, frequencyPenalty: parseFloat(e.target.value) })}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{modelInfo.minFrequencyPenalty ?? -2}</span>
                      <span>Default: {modelInfo.defaultFrequencyPenalty.toFixed(2)}</span>
                      <span>{modelInfo.maxFrequencyPenalty ?? 2}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Reduces repetition based on token frequency. Positive = less repetition
                    </p>
                  </div>
                )}

                {/* Presence Penalty Slider */}
                {modelInfo?.defaultPresencePenalty !== undefined && modelInfo?.defaultPresencePenalty !== null && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Presence Penalty: {tempConfig.presencePenalty?.toFixed(2) ?? modelInfo.defaultPresencePenalty.toFixed(2)}
                    </label>
                    <input
                      type="range"
                      min={modelInfo.minPresencePenalty ?? -2}
                      max={modelInfo.maxPresencePenalty ?? 2}
                      step={0.01}
                      value={tempConfig.presencePenalty ?? modelInfo.defaultPresencePenalty}
                      onChange={(e) => setTempConfig({ ...tempConfig, presencePenalty: parseFloat(e.target.value) })}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{modelInfo.minPresencePenalty ?? -2}</span>
                      <span>Default: {modelInfo.defaultPresencePenalty.toFixed(2)}</span>
                      <span>{modelInfo.maxPresencePenalty ?? 2}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Encourages new topics. Positive = more diverse content
                    </p>
                  </div>
                )}

                {/* Info box about defaults */}
                {tempConfig.modelId && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> Parameters shown are those supported by this model.
                      Defaults are optimized for consistent, deterministic results.
                    </p>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0 bg-white">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveAgentConfig}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* System Prompt Comparison Dialog */}
      <TextComparisonDialog
        isOpen={showPromptComparison}
        title="Compare System Prompts"
        items={promptComparisonItems}
        initialLeftId={tempConfig.systemPromptHash}
        fetchContent={fetchPromptContent}
        onApprove={handlePromptComparisonApprove}
        onClose={() => setShowPromptComparison(false)}
      />
    </div>
  );
};
